import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { SERVICE_OPTIONS, BUDGET_OPTIONS } from "../../data/content";
import { Field, SelectField, inputCls } from "./Fields";
import { Textarea } from "../ui/textarea";

const today = () => new Date().toISOString().slice(0, 10);

function Success({ onReset }) {
  return (
    <div data-testid="consultation-success" className="flex flex-col items-start gap-4 py-8">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-mint/15 text-mint"><CheckCircle2 size={30} /></div>
      <h3 className="text-2xl font-bold text-navy">Your consultation request is in.</h3>
      <p className="text-slate-600">Our team will reach out to confirm your slot. A confirmation has been shared with the Quantora team.</p>
      <button onClick={onReset} data-testid="consultation-book-another-btn" className="text-sm font-semibold text-mint-600 underline-offset-4 hover:underline">Book another consultation</button>
    </div>
  );
}

export default function ConsultationForm({ defaultService = "" }) {
  const { user } = useAuth();
  const blank = { name: user?.name || "", mobile: user?.phone || "", email: user?.email || "", city: user?.city || "", service: defaultService, budget: "", preferred_date: "", message: "" };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, name: f.name || user.name || "", mobile: f.mobile || user.phone || "", email: f.email || user.email || "", city: f.city || user.city || "" }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.budget) return toast.error("Please select a service and budget range.");
    setLoading(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Consultation booked successfully");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success onReset={() => { setForm(blank); setDone(false); }} />;

  return (
    <form onSubmit={submit} data-testid="consultation-form" className="grid gap-5 sm:grid-cols-2">
      <Field label="Full Name" id="c-name"><input id="c-name" required minLength={2} className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="consultation-name-input" /></Field>
      <Field label="Mobile Number" id="c-mobile"><input id="c-mobile" required type="tel" pattern="[+0-9 ]{7,20}" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" data-testid="consultation-mobile-input" /></Field>
      <Field label="Email Address" id="c-email"><input id="c-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="consultation-email-input" /></Field>
      <Field label="City" id="c-city"><input id="c-city" required minLength={2} className={inputCls} value={form.city} onChange={set("city")} placeholder="Mumbai" data-testid="consultation-city-input" /></Field>
      <SelectField label="Service Interested In" id="c-service" value={form.service} onChange={set("service")} options={SERVICE_OPTIONS} placeholder="Select a service" testid="consultation-service-select" />
      <SelectField label="Investment Budget Range" id="c-budget" value={form.budget} onChange={set("budget")} options={BUDGET_OPTIONS} placeholder="Select a range" testid="consultation-budget-select" />
      <Field label="Preferred Consultation Date" id="c-date"><input id="c-date" required type="date" min={today()} className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} data-testid="consultation-date-input" /></Field>
      <div className="sm:col-span-2">
        <Field label="Message" id="c-msg"><Textarea id="c-msg" rows={4} className="rounded-xl border-slate-200 bg-white text-navy focus-visible:ring-gold/40" value={form.message} onChange={set("message")} placeholder="Tell us about your goals or questions (optional)" data-testid="consultation-message-input" /></Field>
      </div>
      <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-md text-xs text-slate-500">By submitting you agree to be contacted by Quantora. Consultations are for education and general guidance.</p>
        <button type="submit" disabled={loading} data-testid="consultation-submit-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-navy-700 disabled:opacity-60">
          {loading && <Loader2 size={16} className="animate-spin" />} Book Your Free Consultation
        </button>
      </div>
    </form>
  );
}
